// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  ifmAnalogValueToModuleStatus,
  ifmBooleanToModuleStatus,
  isDakotaModel,
  operationModeToModuleStatus,
} from '../../../../../../../../plugins/crusher';
import { DASHBOARD_NULLISH_TEXT } from '../../../../../../../../plugins/general';

// Components
import {
  ModuleIFM,
  ModuleIFMLayout,
  ModuleIFMStatus,
  ModuleIFMItem,
} from '../../../../../../../../components/ModuleIFM';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';

// View
import { IFMModuleProps, StatusValue } from './IFMStatus.types';
import {
  CavityLevelContainer,
  ClampPressureContainer,
  Container,
  CountershaftTempContainer,
  CrusherAdjContainer,
  CrusherAdjProx2Container,
  CrusherModulesContainer,
  CrusherParagraphContainer,
  CrusherParagraphsContainer,
  CrusherRightParagraps,
  CrusherSpeedContainer,
  CrusherTextContainer,
  EccentricContainer,
  HydraulicOilTemp,
  HydraulicReservoirLowLevel,
  LeftSection,
  LubeContainer,
  LubeFlowContainer,
  LubeTextsContainer,
  MainLayout,
  ModuleContainer,
  ModuleParagraphContainer,
  NumericContainer,
  ParagraphContainer,
  ReturnTempContainer,
  RightSection,
  SectionColumn,
  SectionContent,
  SupplyTempContainer,
  TrampPressureContainer,
  TrampPressureParagraph,
  VibrationContainer,
  VibrationSensor2Container,
  VibrationSensorContainer,
} from './IFMStatus.style';

function IFMStatus({
  className,
  unitData,
  edgeStatus,
  testId,
}: IFMModuleProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  // Check if the model is Dakota
  const isDakota: boolean = isDakotaModel(unitData?.crusherModelPLC);

  /**
   * Generates items for Module 1A
   */
  const getModule1AItems = (): ModuleIFMItem[] => [
    {
      id: 'i-4',
      text: 'I-4',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputCrLinerContact,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-4',
      text: '0-4',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol7,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-3',
      text: 'I-3',
      status: operationModeToModuleStatus(
        unitData?.operationModeOperateService,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-3',
      text: '0-3',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol6,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-2',
      text: 'I-2',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputHydClampPressurize,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-2',
      text: '0-2',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol2,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-1',
      text: 'I-1',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputHydTrampPressurize,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-1',
      text: '0-1',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol1,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 1B
   */
  const getModule1BItems = (): ModuleIFMItem[] => [
    {
      id: 'i-4',
      text: 'I-4',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputHydTemp,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-4',
      text: '0-4',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol8,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-3',
      text: 'I-3',
      status: ifmBooleanToModuleStatus(
        null,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-3',
      text: '0-3',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol5,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-2',
      text: 'I-2',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputHydLevelLow,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-2',
      text: '0-2',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol4,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-1',
      text: 'I-1',
      status: ifmBooleanToModuleStatus(
        null,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: '0-1',
      text: '0-1',
      status: ifmBooleanToModuleStatus(
        unitData?.dOutputHydSol3,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 2
   */
  const getModule2Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogHydClampPressure,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogHydTrampPressure,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 7
   */
  const getModule7Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogLubeTankTemp,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogLubeReturnTemp,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 8
   */
  const getModule8Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogLubeSupplyTemp,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogLubeFlow,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 9A
   */
  const getModule9AItems = (): ModuleIFMItem[] => [
    {
      id: 'i-3/4',
      text: 'I-3/4',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputLubeFilterPlugged,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-4',
      text: 'I-4',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputLubeFlowSwitch,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-1',
      text: 'I-1',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputLubeTankLevel,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-2',
      text: 'I-2',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputLubeFilterDirty,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 10
   */
  const getModule10Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogVibration4,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogCrEccentricTemp,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 11
   */
  const getModule11Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        isDakota ? unitData?.analogLubeBrakePressure : unitData?.analogLubeOilPressure,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogVibration1,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 12
   */
  const getModule12Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogVibration2,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogVibration3,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 13A
   */
  const getModule13AItems = (): ModuleIFMItem[] => [
    {
      id: 'i-3/4',
      text: 'I-3/4',
      status: ifmBooleanToModuleStatus(
        null,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-4',
      text: 'I-4',
      status: ifmBooleanToModuleStatus(
        null,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-1',
      text: 'I-1',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputCRAdjProx1,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'i-2',
      text: 'I-2',
      status: ifmBooleanToModuleStatus(
        unitData?.dInputCRAdjProx2,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 14
   */
  const getModule14Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogCrusherSpeed,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogCountershaftTemp,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
  ];

  /**
   * Generates items for Module 15
   */
  const getModule15Items = (): ModuleIFMItem[] => [
    {
      id: 'ai-1',
      text: 'AI-1',
      status: ifmAnalogValueToModuleStatus(
        unitData?.analogCavityLevel,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      ),
    },
    {
      id: 'ai-2',
      text: 'AI-2',
      status: ModuleIFMStatus.neutral,
    },
  ];

  /**
   * Renders a status paragraph with value
   *
   * @param {string} name - The status name for translation
   * @param {StatusValue} value - The value to display
   * @param {boolean} isNumeric - Whether the value is numeric
   * @returns {JSX.Element} The rendered status paragraph
  */
  const renderStatus = (
    name: string,
    value?: StatusValue,
    isNumeric: boolean = false,
  ): JSX.Element => {
    const translationKey: string = `view.crusher.maintenance.ifm.modulesStatus.${name}`;

    return (
      <ParagraphContainer>
        <Paragraph size={ParagraphSize.sm}>
          {t(translationKey)}
        </Paragraph>
        {isNumeric && (
          <NumericContainer>
            <Paragraph size={ParagraphSize.sm} weight={ParagraphWeight.medium}>
              {value || DASHBOARD_NULLISH_TEXT}
            </Paragraph>
          </NumericContainer>
        )}
      </ParagraphContainer>
    );
  };

  return (
    <Container className={className} data-testid={testId}>
      <MainLayout>
        <LeftSection>
          <div>
            <Paragraph weight={ParagraphWeight.bold}>
              {t('view.crusher.maintenance.ifm.modulesStatus.comboUnit')}
            </Paragraph>
          </div>
          <SectionContent>
            <SectionColumn>
              <ModuleParagraphContainer>
                {renderStatus('linerContactPressure', unitData?.dInputCrLinerContact)}
                {renderStatus('hydraulicService', unitData?.operationModeOperateService)}
                {renderStatus(
                  'clampPressurizeServiceBox',
                  unitData?.dInputHydClampPressurize,
                )}
                {renderStatus(
                  'trampPressureServiceBox',
                  unitData?.dInputHydTrampPressurize,
                )}
              </ModuleParagraphContainer>

              <HydraulicOilTemp>
                {renderStatus('hydraulicOilHighTemp', unitData?.dInputHydTemp)}
              </HydraulicOilTemp>
              <HydraulicReservoirLowLevel>
                {renderStatus(
                  'hydraulicReservoirLowLevel',
                  unitData?.dInputHydLevelLow,
                )}
              </HydraulicReservoirLowLevel>

              <ClampPressureContainer>
                {renderStatus('clampPressure', unitData?.analogHydClampPressure, true)}
              </ClampPressureContainer>
              <TrampPressureContainer>
                {renderStatus('tankTemp', unitData?.analogHydTrampPressure, true)}
              </TrampPressureContainer>
              <SupplyTempContainer>
                {renderStatus('supplyTemp', unitData?.analogLubeSupplyTemp, true)}
              </SupplyTempContainer>
              <LubeContainer>
                {renderStatus('lubeFilterPlugged', unitData?.dInputLubeFilterPlugged)}
                {renderStatus('lubeTankLevelOk', unitData?.dInputLubeTankLevel)}
              </LubeContainer>
            </SectionColumn>

            <div>
              <ModuleContainer>
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="1A"
                  layout={ModuleIFMLayout.column}
                  items={getModule1AItems()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="1B"
                  layout={ModuleIFMLayout.column}
                  items={getModule1BItems()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="2"
                  layout={ModuleIFMLayout.column}
                  items={getModule2Items()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="7"
                  layout={ModuleIFMLayout.column}
                  items={getModule7Items()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="8"
                  layout={ModuleIFMLayout.column}
                  items={getModule8Items()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="9A"
                  layout={ModuleIFMLayout.row}
                  items={getModule9AItems()}
                />
              </ModuleContainer>
            </div>

            <div>
              <CrusherTextContainer>
                {renderStatus('bowlClose', unitData?.dOutputHydSol7)}
                {renderStatus('adjustmentUnderload', unitData?.dOutputHydSol6)}
                {renderStatus('trampPressurize', unitData?.dOutputHydSol2)}
                {renderStatus('systemPressurize', unitData?.dOutputHydSol1)}
              </CrusherTextContainer>
              <CrusherParagraphContainer>
                {renderStatus('bowlOpen', unitData?.dOutputHydSol8)}
                {renderStatus('clampPressureUnload', unitData?.dOutputHydSol5)}
                {renderStatus('clampPressurize', unitData?.dOutputHydSol4)}
                {renderStatus('trampClear', unitData?.dOutputHydSol3)}
              </CrusherParagraphContainer>
              <TrampPressureParagraph>
                {renderStatus('trampPressure', unitData?.analogHydTrampPressure, true)}
              </TrampPressureParagraph>
              <ReturnTempContainer>
                {renderStatus('returnTemp', unitData?.analogLubeReturnTemp, true)}
              </ReturnTempContainer>
              <LubeFlowContainer>
                {renderStatus('lubeFlow', unitData?.analogLubeFlow, true)}
              </LubeFlowContainer>
              <LubeTextsContainer>
                {renderStatus('lubeFlowSwitch', unitData?.dInputLubeFlowSwitch)}
                {renderStatus('lubeFilterDirty', unitData?.dInputLubeFilterDirty)}
              </LubeTextsContainer>
            </div>

          </SectionContent>
        </LeftSection>
        <RightSection>
          <div>
            <Paragraph weight={ParagraphWeight.bold}>
              {t('view.crusher.maintenance.crusher')}
            </Paragraph>
          </div>
          <SectionContent>
            <div>
              <CrusherParagraphsContainer>
                {renderStatus('vibrationSensor4', unitData?.analogVibration4, true)}
                <EccentricContainer>
                  {renderStatus(
                    isDakota ? 'antiSpin' : 'eccentricSupplyPressure',
                    isDakota
                      ? unitData?.analogLubeBrakePressure
                      : unitData?.analogLubeOilPressure,
                    true,
                  )}
                </EccentricContainer>
              </CrusherParagraphsContainer>
              <VibrationContainer>
                {renderStatus('vibrationSensor2', unitData?.analogVibration2, true)}
              </VibrationContainer>
              <CrusherAdjContainer>
                {renderStatus('crusherAdjProx1', unitData?.dInputCRAdjProx1)}
              </CrusherAdjContainer>
              <CrusherSpeedContainer>
                {renderStatus('crusherSpeed', unitData?.analogCrusherSpeed, true)}
              </CrusherSpeedContainer>
              <CavityLevelContainer>
                {renderStatus('cavityLevel', unitData?.analogCavityLevel, true)}
              </CavityLevelContainer>
            </div>

            <div>
              <CrusherModulesContainer>
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="10"
                  layout={ModuleIFMLayout.column}
                  items={getModule10Items()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="11"
                  layout={ModuleIFMLayout.column}
                  items={getModule11Items()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="12"
                  layout={ModuleIFMLayout.column}
                  items={getModule12Items()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="13A"
                  layout={ModuleIFMLayout.column}
                  items={getModule13AItems()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="14"
                  layout={ModuleIFMLayout.column}
                  items={getModule14Items()}
                />
                <ModuleIFM
                  testId="control-mode"
                  className={className}
                  centralValue="15"
                  layout={ModuleIFMLayout.column}
                  items={getModule15Items()}
                />
              </CrusherModulesContainer>
            </div>

            <div>
              <CrusherRightParagraps>
                {renderStatus('eccentricShaftTemp', unitData?.analogCrEccentricTemp, true)}
                <VibrationSensorContainer>
                  {renderStatus('vibrationSensor1', unitData?.analogVibration1, true)}
                </VibrationSensorContainer>
              </CrusherRightParagraps>
              <VibrationSensor2Container>
                {renderStatus('vibrationSensor3', unitData?.analogVibration3, true)}
              </VibrationSensor2Container>
              <CrusherAdjProx2Container>
                {renderStatus('crusherAdjProx2', unitData?.dInputCRAdjProx2)}
              </CrusherAdjProx2Container>
              <CountershaftTempContainer>
                {renderStatus('countershaftTemp', unitData?.analogCountershaftTemp, true)}
              </CountershaftTempContainer>
            </div>
          </SectionContent>
        </RightSection>
      </MainLayout>
    </Container>
  );
}

export { IFMStatus };
